import { useState, useEffect, type ReactNode } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { GgcButton } from '@/components/ui/ggc/button';
import { GgcInput } from '@/components/ui/ggc/input';
import ChannelTypeIcon from '@/components/icons/ChannelTypeIcon';
import Tooltip from '@/components/Tooltip';
import CountryFlag from '@/components/CountryFlag';
import SingleSelect, { type SingleSelectOption } from '@/components/SingleSelect';
import { savePanelUser } from '@/lib/api/panel.api';
import { COUNTRY_ISO } from '@/lib/constants/countryIso';
import { useI18nText } from '@/lib/utils/i18nText';
import { useAlert } from '@/lib/utils/useAlert';
import { Save, ChevronDown } from 'lucide-react';
import { BsEnvelopeSlash } from 'react-icons/bs';

interface EditChannelUser {
  _id: string;
  id?: string;
  email?: string;
  contactEmail?: string;
  selfProfile?: { email?: string };
  skype?: string;
  paypalEmail?: string;
  country?: string;
  lng?: string;
  socials?: Record<string, string | undefined>;
  blacklistedReason?: string;
  memo?: string;
}

interface EditFormData {
  email: string; contactEmail: string; selfProfileEmail: string; skype: string; paypalEmail: string;
  country: string; lng: string;
  youtube: string; twitch: string; twitter: string; facebook: string;
  instagram: string; tiktok: string; douyin: string; discord: string; telegram: string;
  bilibili: string; chzzk: string; afreeca: string;
  blacklistedReason: string; memo: string;
}

export interface EditChannelDialogProps {
  open: boolean;
  user: EditChannelUser;
  onClose: () => void;
  onSaved?: () => void;
}

function normalizeSocialUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  return `https://${trimmed}`;
}

/** Returns '을' or '를' based on the last character of the Korean string. */
function eul(text: string): '을' | '를' {
  const code = text.charCodeAt(text.length - 1);
  if (code >= 0xAC00 && code <= 0xD7A3) return (code - 0xAC00) % 28 !== 0 ? '을' : '를';
  return /[aeiouAEIOU]/.test(text[text.length - 1]) ? '를' : '을';
}

const NO_CONTACT_EMAIL = 'nocontact@ggcontent.com';
// Matches EmailCell.tsx's EMAIL_RE — admin/panel/save-user (this dialog's endpoint) requires a
// non-empty, valid `email`; `contactEmail`/`selfProfileEmail` are optional but must be valid if
// present. Keep validation in sync with EmailCell.tsx, the other editor for these same 3 fields.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const COUNTRY_OPTIONS: SingleSelectOption[] = COUNTRY_ISO.map((c) => ({
  value: c.value,
  label: `${c.value} — ${c.text}`,
  triggerLabel: c.value,
  prefix: <CountryFlag countryCode={c.value} size={14} />,
}));

export function EditChannelDialog({ open, user, onClose, onSaved }: EditChannelDialogProps) {
  const { t } = useI18nText();
  const showAlert = useAlert();
  const [saving, setSaving] = useState(false);
  const [socialExpanded, setSocialExpanded] = useState(true);
  const [noContactPopover, setNoContactPopover] = useState<keyof EditFormData | null>(null);
  const [error, setError] = useState<string | null>(null);
  /* Which field the current error belongs to, so only that field's input gets the red border. */
  const [errorField, setErrorField] = useState<keyof EditFormData | null>(null);
  const EMPTY_FORM: EditFormData = {
    email: '', contactEmail: '', selfProfileEmail: '', skype: '', paypalEmail: '',
    country: '', lng: '',
    youtube: '', twitch: '', twitter: '', facebook: '',
    instagram: '', tiktok: '', douyin: '', discord: '', telegram: '',
    bilibili: '', chzzk: '', afreeca: '',
    blacklistedReason: '', memo: '',
  };
  const [form, setForm] = useState<EditFormData>(EMPTY_FORM);
  /* Snapshot of the form as loaded — Save stays disabled until the form differs. */
  const [committedForm, setCommittedForm] = useState<EditFormData>(EMPTY_FORM);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setErrorField(null);
    const next: EditFormData = {
      email: user.email ?? '',
      contactEmail: user.contactEmail ?? '',
      selfProfileEmail: user.selfProfile?.email ?? '',
      skype: user.skype ?? '',
      paypalEmail: user.paypalEmail ?? '',
      country: user.country ?? '',
      lng: user.lng ?? '',
      youtube: user.socials?.youtube_com ?? '',
      twitch: user.socials?.twitch_tv ?? '',
      twitter: user.socials?.twitter_com ?? '',
      facebook: user.socials?.facebook_com ?? '',
      instagram: user.socials?.instagram_com ?? user.socials?.www_instagram_com ?? '',
      tiktok: user.socials?.tiktok_com ?? '',
      douyin: user.socials?.douyin_com ?? '',
      discord: user.socials?.discord_gg ?? '',
      telegram: user.socials?.t_me ?? '',
      bilibili: user.socials?.space_bilibili_com ?? user.socials?.bilibili_com ?? '',
      chzzk: user.socials?.chzzk_naver_com ?? '',
      afreeca: user.socials?.afreecatv_com ?? '',
      blacklistedReason: user.blacklistedReason ?? '',
      memo: user.memo ?? '',
    };
    setForm(next);
    setCommittedForm(next);
  }, [open, user]);

  const dirty = (Object.keys(committedForm) as (keyof EditFormData)[]).some((k) => form[k] !== committedForm[k]);

  const handleChange = (field: keyof EditFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (error && field === errorField) { setError(null); setErrorField(null); }
  };

  const handleSave = async () => {
    const email = form.email.trim().toLowerCase();
    const contactEmail = form.contactEmail.trim().toLowerCase();
    const selfProfileEmail = form.selfProfileEmail.trim().toLowerCase();
    // Registration email is required (admin/panel/save-user 400s on blank); contact/
    // self-reported stay optional — mirrors EmailCell.tsx's handleSave validation.
    if (!email) {
      setError(t("Registration email can't be empty — enter the corrected email instead of deleting it", '가입 이메일은 비워둘 수 없습니다 — 삭제 대신 올바른 이메일로 수정해 주세요'));
      setErrorField('email');
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setError(t('Enter a valid registration email', '올바른 가입 이메일을 입력하세요'));
      setErrorField('email');
      return;
    }
    if (contactEmail && !EMAIL_RE.test(contactEmail)) {
      setError(t('Enter a valid contact email', '올바른 연락처 이메일을 입력하세요'));
      setErrorField('contactEmail');
      return;
    }
    if (selfProfileEmail && !EMAIL_RE.test(selfProfileEmail)) {
      setError(t('Enter a valid self-reported email', '올바른 본인 입력 이메일을 입력하세요'));
      setErrorField('selfProfileEmail');
      return;
    }
    setError(null);
    setErrorField(null);
    setSaving(true);
    try {
      await savePanelUser({
        userId: user._id || user.id || '',
        email,
        contactEmail,
        selfProfileEmail,
        skype: form.skype,
        paypalEmail: form.paypalEmail,
        country: form.country,
        lng: form.lng,
        blacklistedReason: form.blacklistedReason,
        memo: form.memo,
        youtube: normalizeSocialUrl(form.youtube),
        twitch: normalizeSocialUrl(form.twitch),
        twitter: normalizeSocialUrl(form.twitter),
        facebook: normalizeSocialUrl(form.facebook),
        instagram: normalizeSocialUrl(form.instagram),
        tiktok: normalizeSocialUrl(form.tiktok),
        douyin: normalizeSocialUrl(form.douyin),
        discord: normalizeSocialUrl(form.discord),
        telegram: normalizeSocialUrl(form.telegram),
        bilibili: normalizeSocialUrl(form.bilibili),
        chzzk: normalizeSocialUrl(form.chzzk),
        afreeca: normalizeSocialUrl(form.afreeca),
      });
      onClose();
      onSaved?.();
    } catch {
      showAlert(t('Failed to save channel details', '채널 정보 저장에 실패했습니다'));
    } finally {
      setSaving(false);
    }
  };

  const fieldRow = (
    label: string,
    field: keyof EditFormData,
    opts?: { type?: string; placeholder?: string; full?: boolean; icon?: ReactNode; noContact?: boolean; compact?: boolean; tooltip?: ReactNode },
  ) => (
    <div className={opts?.full ? 'up-dialog-field up-dialog-field--full' : 'up-dialog-field'}>
      {!opts?.compact && (
      <label className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5">
          {opts?.icon}
          <span>{label}</span>
          {opts?.tooltip && (
            <Tooltip content={opts.tooltip}>
              <i className="fa fa-info-circle up-dialog-field-tooltip-icon" aria-hidden />
            </Tooltip>
          )}
        </span>
        {opts?.noContact && (
          <div className="relative">
            <Tooltip
              side="bottom"
              content={t(
                `Fills this field with a no-contact placeholder (${NO_CONTACT_EMAIL}) to mark that this influencer has no reachable email.`,
                `이 필드를 연락 불가 표시용 주소(${NO_CONTACT_EMAIL})로 채워, 연락 가능한 이메일이 없음을 표시합니다.`,
              )}
            >
              <button
                type="button"
                className="up-dialog-nocontact-btn"
                onClick={() => setNoContactPopover(noContactPopover === field ? null : field)}
              >
                <BsEnvelopeSlash className="mr-1 text-[12px]" />
                No Contact
              </button>
            </Tooltip>
            {noContactPopover === field && (
              <div className="absolute right-0 top-full z-10 mt-1 w-64 rounded-ggc-sm border border-ggc-border-gray bg-white p-3 shadow-ggc-md">
                <p className="mb-2 ggc-text-caption-m text-ggc-text-primary">
                  {t(
                    `Fill with ${NO_CONTACT_EMAIL}?`,
                    `${NO_CONTACT_EMAIL} 으로 채울까요?`,
                  )}
                </p>
                <div className="flex justify-end gap-1.5">
                  <button
                    type="button"
                    className="up-dialog-btn up-dialog-btn--sm"
                    onClick={() => setNoContactPopover(null)}
                  >
                    {t('Cancel', '취소')}
                  </button>
                  <button
                    type="button"
                    className="up-dialog-btn up-dialog-btn--sm up-dialog-btn--primary"
                    onClick={() => { handleChange(field, NO_CONTACT_EMAIL); setNoContactPopover(null); }}
                  >
                    {t('Confirm', '확인')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </label>
      )}
      {field === 'memo' ? (
        <textarea
          className="filter-input"
          style={{ height: 'auto' }}
          rows={4}
          value={form[field]}
          placeholder={t(`Enter ${label}`, `${label}${eul(label)} 입력하세요`)}
          onChange={(e) => handleChange(field, e.target.value)}
        />
      ) : (
        <GgcInput
          type={opts?.type ?? 'text'}
          value={form[field]}
          error={errorField === field}
          placeholder={opts?.placeholder ?? t(`Enter ${label}`, `${label}${eul(label)} 입력하세요`)}
          onChange={(e) => handleChange(field, e.target.value)}
          startIcon={opts?.compact ? opts?.icon : undefined}
        />
      )}
      {errorField === field && error && <p className="email-edit-error">{error}</p>}
    </div>
  );

  const socialField = (label: string, field: keyof EditFormData, channelType: string) =>
    fieldRow(label, field, {
      icon: <ChannelTypeIcon type={channelType} size={14} />,
      placeholder: t(`Enter ${label} URL`, `${label} URL을 입력하세요`),
      compact: true,
    });

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent
        className="max-w-[760px] h-[95vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>{t('Edit Channel Basic Info.', '채널 기본 정보 편집')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('Edit this channel\'s registered account details.', '이 채널의 등록 계정 정보를 편집합니다.')}
          </DialogDescription>
        </DialogHeader>
        <div className="up-dialog-grid mt-3">
          {fieldRow(t('Registration email', '가입 이메일'), 'email', {
            type: 'email', icon: <i className="fa fa-envelope-o" aria-hidden />, noContact: true,
            tooltip: t(
              'The email used to sign up / log into this account.',
              '이 계정을 가입·로그인할 때 사용하는 이메일입니다.',
            ),
          })}
          {fieldRow(t('Contact email', '연락처 이메일'), 'contactEmail', {
            type: 'email', icon: <i className="fa fa-envelope-o" aria-hidden />, noContact: true,
            tooltip: t(
              'A separate address for GGC to reach out to this influencer, if different from their registration email.',
              '가입 이메일과 다를 경우 GGC가 이 인플루언서에게 연락할 때 사용하는 별도 이메일입니다.',
            ),
          })}
          {fieldRow(t('Self-reported email', '본인 입력 이메일'), 'selfProfileEmail', {
            type: 'email', icon: <i className="fa fa-envelope-o" aria-hidden />,
            tooltip: t(
              'Entered by the influencer themselves via their self-service profile — not verified by an admin.',
              '인플루언서가 본인 프로필 폼에서 직접 입력한 이메일이며, 관리자가 검증한 값이 아닙니다.',
            ),
          })}
          {fieldRow(t('Skype', '스카이프'), 'skype', { icon: <i className="fa fa-skype" aria-hidden /> })}
          {fieldRow(t('Paypal email', '페이팔 이메일'), 'paypalEmail', {
            type: 'email', icon: <i className="fa fa-paypal" aria-hidden />,
            tooltip: t(
              'The PayPal account that real money is sent to when an admin approves a payment request — double-check this before approving.',
              '관리자가 결제 요청을 승인하면 실제로 돈이 송금되는 PayPal 계정입니다 — 승인 전 반드시 다시 확인하세요.',
            ),
          })}
          <div className="up-dialog-field">
            <label>
              <span className="inline-flex items-center gap-1.5">
                <i className="fa fa-globe" aria-hidden />
                <span>{t('Country', '국가')}</span>
              </span>
            </label>
            <SingleSelect
              options={COUNTRY_OPTIONS}
              value={form.country}
              onChange={(v) => handleChange('country', v)}
              placeholder={t('Select country', '국가 선택')}
              className="w-full"
            />
          </div>
          {fieldRow(t('Language', '언어'), 'lng', {
            placeholder: 'en, de, fr, etc.', icon: <i className="fa fa-language" aria-hidden />,
          })}
          <div className="up-dialog-section up-dialog-section--collapsible">
            <span>{t('Social Links', '소셜 링크')}</span>
            <button
              type="button"
              className="up-dialog-section__toggle"
              aria-expanded={socialExpanded}
              aria-label={socialExpanded ? t('Collapse', '접기') : t('Expand', '펼치기')}
              onClick={() => setSocialExpanded((v) => !v)}
            >
              <ChevronDown className={`h-4 w-4 transition-transform ${socialExpanded ? 'rotate-180' : ''}`} />
            </button>
          </div>
          {socialExpanded && (<>
            {socialField('YouTube', 'youtube', 'youtube')}
            {socialField('Twitch', 'twitch', 'twitch')}
            {socialField('Twitter', 'twitter', 'twitter')}
            {socialField('Facebook', 'facebook', 'facebook')}
            {socialField('Instagram', 'instagram', 'instagram')}
            {socialField('TikTok', 'tiktok', 'tiktok')}
            {socialField('Douyin', 'douyin', 'douyin')}
            {socialField('Discord', 'discord', 'discord')}
            {socialField('Telegram', 'telegram', 'telegram')}
            {socialField('Bilibili', 'bilibili', 'bilibili')}
            {socialField('CHZZK', 'chzzk', 'chzzk')}
            {socialField('SOOP (AfreecaTV)', 'afreeca', 'afreeca')}
          </>)}
        </div>
        <div className="flex flex-col gap-3">
          <div className="up-dialog-actions">
            <GgcButton variant="secondary" size='sm' onClick={onClose}>{t('Cancel', '취소')}</GgcButton>
            <GgcButton variant="primary" size='sm' onClick={() => void handleSave()} disabled={saving || !dirty}>
              <Save className="h-3.5 w-3.5" />
              {saving ? t('Saving...', '저장 중...') : t('Save', '저장')}
            </GgcButton>
          </div>
          <div className="up-dialog-section"></div>
          {fieldRow(t('Memo', '메모'), 'memo', {
            full: true, icon: <i className="fa fa-file-text-o" aria-hidden />,
          })}
          {fieldRow(t('Blacklisted reason', '블랙리스트 사유'), 'blacklistedReason', {
            full: true, icon: <i className="fa fa-ban" aria-hidden />,
          })}

        </div>
      </DialogContent>
    </Dialog>
  );
}
